import React, { useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import useMccCodes from 'api/hooks/use-mcc-codes'

const MccCodePicker = ({ selectedCodes, onChange, disabled }) => {
  const { data: mccCodes, isLoading } = useMccCodes()
  const [show, setShow] = useState(false)
  const [search, setSearch] = useState('')
  const [localSelected, setLocalSelected] = useState(new Set())

  const handleOpen = () => {
    setLocalSelected(new Set(selectedCodes || []))
    setSearch('')
    setShow(true)
  }

  const handleClose = () => {
    setShow(false)
  }

  const handleSave = () => {
    onChange(Array.from(localSelected))
    setShow(false)
  }

  const filteredCodes = useMemo(() => {
    if (!mccCodes) return []
    if (!search) return mccCodes
    const lower = search.toLowerCase()
    return mccCodes.filter((item) => {
      const label = item.description || item.category || ''
      return (
        label.toLowerCase().includes(lower) ||
        (item.grouping || '').toLowerCase().includes(lower) ||
        item.mcc.includes(search)
      )
    })
  }, [mccCodes, search])

  const groupedCodes = useMemo(() => {
    const groups = new Map()
    filteredCodes.forEach((item) => {
      const key = item.grouping || 'Uncategorized'
      if (!groups.has(key)) groups.set(key, [])
      groups.get(key).push(item)
    })
    return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b))
  }, [filteredCodes])

  const handleToggle = (mcc) => {
    setLocalSelected((prev) => {
      const next = new Set(prev)
      if (next.has(mcc)) next.delete(mcc)
      else next.add(mcc)
      return next
    })
  }

  const handleToggleGroup = (groupItems) => {
    const codesInGroup = groupItems.map((i) => i.mcc)
    const allSelected = codesInGroup.every((c) => localSelected.has(c))
    setLocalSelected((prev) => {
      const next = new Set(prev)
      if (allSelected) codesInGroup.forEach((c) => next.delete(c))
      else codesInGroup.forEach((c) => next.add(c))
      return next
    })
  }

  const handleSelectDefaults = () => {
    if (!mccCodes) return
    setLocalSelected(
      new Set(mccCodes.filter((i) => i.restricted === 'yes').map((i) => i.mcc))
    )
  }

  const handleClearAll = () => setLocalSelected(new Set())

  const selectedCount = (selectedCodes || []).length

  return (
    <>
      <Button
        variant="light"
        size="sm"
        onClick={handleOpen}
        disabled={disabled || isLoading}
      >
        {isLoading ? 'Loading...' : `${selectedCount} MCC codes selected`}
      </Button>

      <Modal show={show} onHide={handleClose} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Restricted MCC Codes</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex gap-2 mb-2">
            <Form.Control
              type="text"
              size="sm"
              placeholder="Search by code, description, or group..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Button
              variant="light"
              size="sm"
              onClick={handleSelectDefaults}
              className="text-nowrap"
            >
              Select All Defaults
            </Button>
            <Button
              variant="light"
              size="sm"
              onClick={handleClearAll}
              className="text-nowrap"
            >
              Clear All
            </Button>
          </div>
          <div className="text-muted small mb-2">
            {localSelected.size} of {mccCodes?.length || 0} codes selected
            {search && ` (showing ${filteredCodes.length} matches)`}
          </div>
          <div
            style={{
              maxHeight: '400px',
              overflowY: 'auto',
              border: '1px solid #dee2e6',
              borderRadius: '4px'
            }}
          >
            {groupedCodes.map(([groupName, items]) => {
              const codesInGroup = items.map((i) => i.mcc)
              const selectedInGroup = codesInGroup.filter((c) =>
                localSelected.has(c)
              ).length
              const allSelected =
                selectedInGroup === codesInGroup.length && codesInGroup.length > 0
              const someSelected =
                selectedInGroup > 0 && selectedInGroup < codesInGroup.length

              return (
                <div key={groupName} style={{ borderBottom: '1px solid #dee2e6' }}>
                  <div
                    className="d-flex align-items-center px-2 py-2 bg-light"
                    style={{ position: 'sticky', top: 0, zIndex: 1 }}
                  >
                    <Form.Check
                      type="checkbox"
                      id={`mcc-group-${groupName}`}
                      checked={allSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = someSelected
                      }}
                      onChange={() => handleToggleGroup(items)}
                      label={
                        <span className="small fw-bold">
                          {groupName}
                          <span className="text-muted fw-normal ms-2">
                            ({selectedInGroup} of {items.length} selected)
                          </span>
                        </span>
                      }
                    />
                  </div>
                  {items.map((item) => (
                    <div
                      key={item.mcc}
                      className="d-flex align-items-center px-4 py-1"
                      style={{ borderTop: '1px solid #f0f0f0' }}
                    >
                      <Form.Check
                        type="checkbox"
                        id={`mcc-${item.mcc}`}
                        checked={localSelected.has(item.mcc)}
                        onChange={() => handleToggle(item.mcc)}
                        label={
                          <span className="small">
                            <strong>{item.mcc}</strong>
                            {' - '}
                            {item.description || item.category}
                            {item.restricted === 'yes' && (
                              <span className="text-muted ms-1">
                                (default restricted)
                              </span>
                            )}
                          </span>
                        }
                      />
                    </div>
                  ))}
                </div>
              )
            })}
            {groupedCodes.length === 0 && (
              <div className="text-muted text-center py-3 small">
                No matching MCC codes found
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave}>
            Save Selection
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

MccCodePicker.propTypes = {
  selectedCodes: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool
}

export default MccCodePicker
